import React, { useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { Chip, PageFilterBar, SearchInput, Segmented, StatePanel, Tabs } from "../../components/ui";
import { GROUPS, REGIONS, TOP_PICK, ageOf, topPicks } from "./dealsData";
import { DealGrid, DealsInfo, SearchWithInfo } from "./dealsParts";
import { useContactedListings } from "./ContactSeller";
import SellerCard from "./SellerCard";

const matches = (o, q) => {
  if (!q) return true;
  const s = `${o.brand || ""} ${o.model || ""} ${o.reference || ""}`.toLowerCase();
  return q.toLowerCase().split(/\s+/).every((w) => s.includes(w));
};

// The "Show" chips: each is a filter and its count (they replace the KPI tiles, Alex 2026-10-29).
// The ⓘ beside search. Selection rule = the box's daily_report.py (backend .claude/features/buy-opportunity.md).
const PS_INFO = [
  ["Source", "Chrono24 private sellers · time of the last update at the top"],
  ["Gets in", "Price ≤ 5th-cheapest online price, same reference, last 6 days"],
  ["−% / +%", "Vs 12-month trade price · label only"],
  ["Age", "New · 5d in report · ↻ 8+ days"],
  ["★ Top pick", `${TOP_PICK.minGap}–${TOP_PICK.maxGap}% under · core brand or B2B lead · not contacted`],
  ["Search", "Reference, brand, model · all words"],
];

// "top" matches the day's top picks (dealsData isTopPick/topPicks), resolved in the tab.
const SHOW = [
  { value: "all", label: "All", match: () => true },
  { value: "top", label: "★ Top picks", match: null },
  { value: "new", label: "New", match: (o) => ageOf(o)?.kind === "new" },
  { value: "week", label: "This week", match: (o) => ageOf(o)?.kind === "week" },
  { value: "recurring", label: "Recurring 8d+", match: (o) => ageOf(o)?.kind === "recurring" },
  { value: "drop", label: "Price dropped", match: (o) => o.history?.price_drop > 0 },
];

// Group by age: when the listing first appeared in the report. "Other" only when the API sent
// no history and the listing is not fresh (a report read before the history field existed).
const AGE_SECTIONS = [
  { value: "new", label: "New" },
  { value: "week", label: "This week" },
  { value: "recurring", label: "Recurring" },
  { value: "other", label: "Other listings" },
];

const GROUP_BY = [
  { value: "age", label: "Age" },
  { value: "brand", label: "Brand group" },
];

const byGap = (a, b) => (Number(b.discount_pct) || -999) - (Number(a.discount_pct) || -999);

// Latest to oldest (Alex, 2026-11-02): first seen in the report, newest first; then listed on
// Chrono24, newest first; then the biggest gap.
const seenAt = (o) => o.history?.first_seen || "";
const byAge = (a, b) => seenAt(b).localeCompare(seenAt(a))
  || String(b.listed_at || "").localeCompare(String(a.listed_at || ""))
  || byGap(a, b);

// [brand, rows][] in the order the rows come in.
const byBrand = (rows) => {
  const m = new Map();
  rows.forEach((o) => { const k = o.brand || "Unknown"; if (!m.has(k)) m.set(k, []); m.get(k).push(o); });
  return [...m.entries()];
};

const Cards = ({ label, rows, picks }) => (
  <DealGrid label={label}>{rows.map((o) => <li key={o.id}><SellerCard o={o} top={picks.has(o.id)} /></li>)}</DealGrid>
);

const Section = ({ id, title, count, children }) => (
  <section className="deals-stack" aria-labelledby={id}>
    <h2 id={id} className="deals-h2">{title} <span className="deals-h2__count">· {count}</span></h2>
    {children}
  </section>
);

// region: "uk" | "eu" | "other", or null for every region.
export default function PrivateSellersTab({ state, onRetry, region, onRegion }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [show, setShow] = useState("all");
  const [groupBy, setGroupBy] = useState("age");
  const data = state.data;
  const all = useMemo(() => data?.items || [], [data]);
  const inRegion = useMemo(() => all.filter((o) => (!region || o.region === region) && matches(o, q)), [all, region, q]);
  const contacted = useContactedListings();
  const picks = useMemo(() => topPicks(inRegion, contacted), [inRegion, contacted]);
  const pickIds = useMemo(() => new Set(picks.map((o) => o.id)), [picks]);
  const matchOf = (s) => s.match || ((o) => pickIds.has(o.id));
  const shown = useMemo(() => inRegion.filter(matchOf(SHOW.find((s) => s.value === show) || SHOW[0])), [inRegion, show, pickIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo(() => {
    const sorted = [...shown].sort(byAge);
    if (groupBy === "brand") {
      return GROUPS.map((g) => ({ ...g, rows: sorted.filter((o) => (o.brand_group || "rest") === g.value) }));
    }
    return AGE_SECTIONS.map((s) => ({ ...s, rows: sorted.filter((o) => (ageOf(o)?.kind || "other") === s.value) }));
  }, [shown, groupBy]);

  if (state.status === "loading") return <StatePanel state="loading" loadingShape="card" loadingCount={6} />;
  if (state.status === "error" && !data) return <StatePanel state="error" onRetry={onRetry} headingLevel={2} />;
  if (!data?.date) {
    return <StatePanel state="empty" headingLevel={2} title="No private-seller report yet" description="The report is built from the Chrono24 private-seller scrape." />;
  }

  const regionCount = (r) => all.filter((o) => o.region === r).length;
  const visible = sections.filter((s) => s.rows.length > 0);

  return (
    <div className="deals-stack">
      {/* Same filter row as Stock Alert (PageFilterBar + SearchInput); Group by on the right. */}
      <PageFilterBar label="Filter private sellers"
        search={(
          <SearchWithInfo info={<DealsInfo title="How listings get here" rows={PS_INFO} />}>
            <SearchInput
              size="lg"
              placeholder="Search reference, brand or model"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onSearch={setQ}
              onClear={() => { setText(""); setQ(""); }}
            />
          </SearchWithInfo>
        )}
      >
        <Segmented label="Group by" options={GROUP_BY} value={groupBy} onChange={setGroupBy} />
      </PageFilterBar>

      {/* Three region tabs, each with its flag(s) (Alex, 2026-10-19), on their own row — beside the
          search the third tab clipped. Same flags as Arbitrage: the combined tab shows the US,
          Hong Kong (Asia) and Dubai (Middle East). */}
      <Tabs
        aria-label="Region"
        value={region}
        onChange={(v) => onRegion(v)}
        items={REGIONS.map((r) => ({
          value: r.value,
          count: regionCount(r.value),
          label: (
            <span className="deals-region-tab">
              <span className="deals-region-tab__flags" aria-hidden="true">
                {r.flags.map((f) => <ReactCountryFlag key={f} countryCode={f} svg />)}
              </span>
              {r.label}
            </span>
          ),
        }))}
      />

      <div className="deals-show" role="group" aria-label="Show">
        {SHOW.map((s) => (
          <Chip key={s.value} selected={show === s.value} onClick={() => setShow(s.value)} count={inRegion.filter(matchOf(s)).length}>
            {s.label}
          </Chip>
        ))}
      </div>

      {/* The day's top picks first (only on "All": the Top picks chip already narrows to them). */}
      {show === "all" && picks.length > 0 ? (
        <Section id="ps-top-picks" title="★ Top picks" count={picks.length}>
          <p className="deals-note">
            {TOP_PICK.minGap}–{TOP_PICK.maxGap}% under trade, reliable trade price, core brand or a B2B lead, not yet contacted. Best £ room first, up to {TOP_PICK.perRegion} per region. Act today: check the listing, then Contact and aim for the Offer price.
          </p>
          <Cards label="Top picks" rows={picks} picks={pickIds} />
        </Section>
      ) : null}

      {visible.length === 0
        ? <StatePanel state="no-results" title="No listings match." description={null} />
        : visible.map((s) => (
          <Section key={s.value} id={`ps-${s.value}`} title={s.label} count={s.rows.length}>
            {groupBy === "brand"
              ? byBrand(s.rows).map(([brand, rows]) => (
                <div key={brand} className="deals-brand">
                  <div className="deals-brand__name">{brand} <span className="deals-brand__count">{rows.length} listing{rows.length === 1 ? "" : "s"}</span></div>
                  <Cards label={`${brand} listings`} rows={rows} picks={pickIds} />
                </div>
              ))
              : <Cards label={`${s.label} listings`} rows={s.rows} picks={pickIds} />}
          </Section>
        ))}
    </div>
  );
}
